/**
 * Canonical season/subtype label map aligned with color-palettes.json.
 * Each entry provides a stable ID, display name, parent season, and
 * the undertone bucket the subtype belongs to.
 */

export type Season = 'spring' | 'summer' | 'autumn' | 'winter';
export type UndertoneGroup = 'warm' | 'cool' | 'neutral';

export interface SeasonSubtype {
  /** Stable string ID matching the palette name in color-palettes.json */
  id: string;
  /** Human-readable display label */
  label: string;
  /** Parent season */
  season: Season;
  /** Undertone group this subtype belongs to */
  undertoneGroup: UndertoneGroup;
  /** Index within the parent season array in color-palettes.json */
  paletteIndex: number;
}

export const SEASON_SUBTYPES: SeasonSubtype[] = [
  // Spring
  { id: 'spring.light',  label: 'Light Spring',  season: 'spring', undertoneGroup: 'warm',    paletteIndex: 0 },
  { id: 'spring.bright', label: 'Bright Spring', season: 'spring', undertoneGroup: 'warm',    paletteIndex: 1 },
  { id: 'spring.warm',   label: 'Warm Spring',   season: 'spring', undertoneGroup: 'warm',    paletteIndex: 2 },

  // Summer
  { id: 'summer.light',  label: 'Light Summer',  season: 'summer', undertoneGroup: 'cool',    paletteIndex: 0 },
  { id: 'summer.muted',  label: 'Muted Summer',  season: 'summer', undertoneGroup: 'cool',    paletteIndex: 1 },
  { id: 'summer.cool',   label: 'Cool Summer',   season: 'summer', undertoneGroup: 'cool',    paletteIndex: 2 },

  // Autumn
  { id: 'autumn.dark',   label: 'Dark Autumn',   season: 'autumn', undertoneGroup: 'neutral', paletteIndex: 0 },
  { id: 'autumn.muted',  label: 'Muted Autumn',  season: 'autumn', undertoneGroup: 'neutral', paletteIndex: 1 },
  { id: 'autumn.warm',   label: 'Warm Autumn',   season: 'autumn', undertoneGroup: 'neutral', paletteIndex: 2 },

  // Winter
  { id: 'winter.dark',   label: 'Dark Winter',   season: 'winter', undertoneGroup: 'cool',    paletteIndex: 0 },
  { id: 'winter.bright', label: 'Bright Winter', season: 'winter', undertoneGroup: 'cool',    paletteIndex: 1 },
  { id: 'winter.cool',   label: 'Cool Winter',   season: 'winter', undertoneGroup: 'cool',    paletteIndex: 2 },
];

/** Quick lookup by stable subtype ID */
export const SUBTYPE_BY_ID: Record<string, SeasonSubtype> = Object.fromEntries(
  SEASON_SUBTYPES.map((s) => [s.id, s])
);

/** Quick lookup by display label (matches palette "name" field) */
export const SUBTYPE_BY_LABEL: Record<string, SeasonSubtype> = Object.fromEntries(
  SEASON_SUBTYPES.map((s) => [s.label.toLowerCase(), s])
);

/**
 * Map a season + paletteIndex back to its stable ID.
 */
export function getSubtypeId(season: Season, paletteIndex: number): string | null {
  const match = SEASON_SUBTYPES.find(
    (s) => s.season === season && s.paletteIndex === paletteIndex
  );
  return match?.id ?? null;
}

/**
 * Map a palette name (as stored in color-palettes.json) to its subtype ID.
 */
export function getSubtypeIdByLabel(paletteName: string): string | null {
  return SUBTYPE_BY_LABEL[paletteName.toLowerCase()]?.id ?? null;
}

/**
 * Get the undertone group for a detected season.
 * warm → spring; cool → summer/winter; neutral → autumn.
 * Used as a soft fallback when only undertone is known.
 */
export const UNDERTONE_TO_DEFAULT_SEASON: Record<UndertoneGroup, Season> = {
  warm:    'spring',
  cool:    'summer',
  neutral: 'autumn',
};
